import { FieldValues } from 'react-hook-form';
import {
  AssessmentQuestionSkipLogicEntry,
  AssessmentQuestionSkipLogicMap,
  AssessmentQuestionType,
  AssessmentsQuestionViewModel,
  QuestionIdToQuestionNumberMap,
} from '../types';

/**
 * Creates a mapping from question IDs to their corresponding question numbers.
 * @param questions - The list of assessment questions to map.
 * @returns A record mapping each question's ID to its question number.
 */
export const mapQuestionIdToQuestionNumber = (
  questions: AssessmentsQuestionViewModel[],
): QuestionIdToQuestionNumberMap => {
  const map: QuestionIdToQuestionNumberMap = questions.reduce(
    (accu, question) => ({
      ...accu,
      [question.id]: question.questionNumber,
    }),
    {},
  );
  return map;
};

/**
 * Finds the next target question based on skip logic, starting from the next question
 * Prioritizes required questions, then matches by `questionNext` ID.
 * A `questionNext` target at or before the current question is ignored to prevent
 * backward navigation and self-referential loops, falling back to the next question.
 * Falls back to the next question if no matches are found.
 * @param currentQuestionNumber - The 1-based number of the current question (used as the starting index).
 * @param questionNext - The ID of the intended next question, or `undefined` if not specified.
 * @param questions - The full list of assessment questions.
 * @returns The matched target question, or `null` if none is found.
 */
const getTargetQuestion = (
  currentQuestionNumber: number, // 1 based index
  questionNext: number | undefined, // this is unique identifier of question
  questions: AssessmentsQuestionViewModel[],
) => {
  const nextQuestionIndex = currentQuestionNumber; // converting 1-based to 0-based index
  const nextQuestionReference = questions[nextQuestionIndex] ?? null;

  if (questionNext === undefined) {
    return nextQuestionReference;
  }

  for (let i = 0; i < questions.length; i++) {
    const question = questions[i];
    if (question.id === questionNext && i < nextQuestionIndex) {
      return nextQuestionReference;
    }
    if (
      i >= nextQuestionIndex &&
      (question.isRequired || question.id === questionNext || question.type === 'unsupported')
    ) {
      return question;
    }
  }
  return nextQuestionReference;
};

/**
 * Computes the skip logic entry for a single question.
 * For choice-based questions (checkbox, radio, select), builds per-option skip targets
 * sorted by target question number.
 * For other question types, resolves a single skip target.
 * @param question - The question to compute skip logic for.
 * @param questions - The full list of assessment questions.
 * @returns The skip logic entry containing the next question target(s).
 */
const getSkipLogicForQuestion = (
  question: AssessmentsQuestionViewModel,
  questions: AssessmentsQuestionViewModel[],
): AssessmentQuestionSkipLogicEntry => {
  const skipTargetQuestion = getTargetQuestion(
    question.questionNumber,
    question.questionNext,
    questions,
  );

  switch (question.type) {
    case 'checkbox':
    case 'radio':
    case 'select': {
      // Build skip targets per option by resolving each option's questionNext
      const questionWithSkipOptions = question.options.reduce(
        (accu, option) => {
          const skipTargetQuestionForOption = getTargetQuestion(
            question.questionNumber,
            option.questionNext,
            questions,
          );
          if (skipTargetQuestionForOption) {
            return {
              ...accu,
              options: [
                ...(accu.options ?? []),
                {
                  optionId: option.value,
                  questionNext: skipTargetQuestionForOption.id,
                  questionNextNumber: skipTargetQuestionForOption.questionNumber,
                },
              ],
            };
          }
          return accu;
        },
        {
          questionNext: skipTargetQuestion?.id ?? question.id,
          questionNextNumber: skipTargetQuestion?.questionNumber ?? question.questionNumber,
        } as AssessmentQuestionSkipLogicEntry,
      );
      return {
        ...questionWithSkipOptions,
        // Sort options by target question number in ascending order
        options:
          questionWithSkipOptions.options?.sort(
            (a, b) => a.questionNextNumber - b.questionNextNumber,
          ) ?? [],
      };
    }
    default: {
      // Non-choice questions: resolve a single skip target

      if (skipTargetQuestion) {
        return {
          questionNext: skipTargetQuestion.id,
          questionNextNumber: skipTargetQuestion.questionNumber,
        };
      }

      // Fallback: point to itself (no skip)
      return {
        questionNext: question.id,
        questionNextNumber: question.questionNumber,
      };
    }
  }
};

/**
 * @param questions - The full list of assessment questions.
 * @returns A map of question IDs to their skip logic entries.
 */
export const generateSkipMap = (
  questions: AssessmentsQuestionViewModel[],
): AssessmentQuestionSkipLogicMap =>
  questions.reduce((accu, question) => {
    const skipLogicEntry = getSkipLogicForQuestion(question, questions);
    return {
      ...accu,
      [question.id]: skipLogicEntry,
    };
  }, {});

/**
 * Computes the updated list of disabled (skipped) question numbers when a skip is triggered.
 * Retains any previously skipped questions before the current question, then generates
 * new skip entries for all questions between the current question and the target question.
 * @param skippedQuestions - The current list of skipped question numbers.
 * @param questionNumber - The 1-based number of the question triggering the skip.
 * @param targetQuestionNumber - The 1-based number of the target question to skip to.
 * @returns The updated array of disabled question numbers.
 */
export const getDisabledQuestionNumbers = (
  skippedQuestionNumbers: number[],
  questionNumber: number,
  targetQuestionNumber: number,
) => {
  // Preserve any skips that were set before current question
  const kept = skippedQuestionNumbers.filter((n) => n < questionNumber);

  // Generate new skip entries for all questions between current and target (exclusive)
  const newSkips = Array.from(
    { length: targetQuestionNumber - questionNumber - 1 },
    (_, i) => questionNumber + 1 + i,
  );

  return [...kept, ...newSkips];
};

export const getTargetIdForQuestionWithAnswer = ({
  currentQuestionId,
  questionType,
  value,
  skipMap,
}: {
  currentQuestionId: number;
  questionType: AssessmentQuestionType;
  value: String[] | string | undefined;
  skipMap: AssessmentQuestionSkipLogicMap;
}) => {
  let targetQuestionId: number | null = skipMap[currentQuestionId]?.questionNext ?? null;
  switch (questionType) {
    case 'checkbox':
      targetQuestionId =
        skipMap[currentQuestionId]?.options?.find(
          (option) => Array.isArray(value) && value.includes(option.optionId),
        )?.questionNext ?? targetQuestionId;
      break;
    case 'radio':
    case 'select': {
      targetQuestionId =
        skipMap[currentQuestionId]?.options?.find((option) => value === option.optionId)
          ?.questionNext ?? targetQuestionId;
      break;
    }
    default:
      break;
  }

  return targetQuestionId;
};

export const getSkippedQuestionsFromAnswers = ({
  questions,
  formValues,
  skipMap,
  questionIdToNumberMap,
}: {
  questions: AssessmentsQuestionViewModel[];
  formValues: FieldValues;
  skipMap: AssessmentQuestionSkipLogicMap;
  questionIdToNumberMap: QuestionIdToQuestionNumberMap;
}) => {
  const skippedSet = new Set<number>();

  questions.forEach((question) => {
    const { id, type, questionNumber } = question;
    if (skippedSet.has(questionNumber)) {
      return;
    }

    const value = formValues[id.toString()] as String[] | string | undefined;
    const targetQuestionId = getTargetIdForQuestionWithAnswer({
      currentQuestionId: id,
      questionType: type,
      value,
      skipMap,
    });

    if (targetQuestionId === null) {
      return;
    }

    const targetQuestionNumber = questionIdToNumberMap[targetQuestionId];
    if (!targetQuestionNumber || targetQuestionNumber <= questionNumber + 1) {
      return;
    }

    for (let qNumber = questionNumber + 1; qNumber < targetQuestionNumber; qNumber++) {
      skippedSet.add(qNumber);
    }
  });

  return Array.from(skippedSet).sort((a, b) => a - b);
};
